import axios from "axios";
import confetti from "canvas-confetti";
import { Check, Crown, Download, Flame, Radar, RotateCcw, Snowflake, Volume2, VolumeX, Zap } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { GameFullscreenButton } from "./GameFullscreenButton";
import { Link } from "react-router-dom";
import { BINGO_COLUMNS, BINGO_SQUARES } from "../data/dolosBingo";
import { fetchMe, getCachedUser } from "../utils/auth";
import { downloadBingoVictoryCard } from "../utils/shareCard";
import { BingoChampionHistory } from "./BingoChampionHistory";
import { BingoLeaderboard } from "./BingoLeaderboard";
import { BingoRaceBoard } from "./BingoRaceBoard";
import { SectionHeading } from "./SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const bingoApi = axios.create({ baseURL: API, withCredentials: true });
const LOCAL_PROGRESS_KEY = "dolopals-bingo-progress";
const EXP_GEAR_KEY = "dolopals-expeditions";
const readDaubGear = (raw) => {
  try {
    const exp = raw && typeof raw === "object" ? raw : JSON.parse(localStorage.getItem(EXP_GEAR_KEY) || "{}");
    const valid = (value, prefix) => (typeof value === "string" && value.startsWith(prefix) ? value : null);
    return { daub: valid(exp.daub, "daub-"), stamp: valid(exp.stamp, "stamp-") };
  } catch {
    return { daub: null, stamp: null };
  }
};
const FREE_INDEX = 2;
const START_SCORE = 100;
const ROUND_SECONDS = 30;
const CALL_LETTERS = ["D", "O", "L", "O", "S"];
const LINES = [
  ...Array.from({ length: 5 }, (_, row) => Array.from({ length: 5 }, (_, col) => row * 5 + col)),
  ...Array.from({ length: 5 }, (_, col) => Array.from({ length: 5 }, (_, row) => row * 5 + col)),
  [0, 6, 12, 18, 24],
  [4, 8, 12, 16, 20],
];

const numberFor = (index) => String((index % 5) * 5 + Math.floor(index / 5) + 1).padStart(2, "0");
const callFor = (index) => `${CALL_LETTERS[index % 5]}-${numberFor(index)}`;
const availableCalls = (cards, skippedSet) =>
  BINGO_SQUARES.map((_, index) => index).filter((index) =>
    index !== FREE_INDEX
    && !skippedSet.has(index)
    && cards.some((card) => !card.marked.has(card.order.indexOf(index))));
const isDiagonal = (line) => (line[0] === 0 && line[1] === 6) || (line[0] === 4 && line[1] === 8);
const isRow = (line) => line.every((value, index) => value === line[0] + index);
const randomFrom = (pool) => pool[Math.floor(Math.random() * pool.length)];
const completedLinesFor = (markedSet) => LINES.filter((line) => line.every((index) => markedSet.has(index)));
const victoryTypesFor = (markedSet) => {
  const lines = completedLinesFor(markedSet);
  const types = new Set();
  if (lines.some(isRow)) types.add("corporate");
  if (lines.some(isDiagonal)) types.add("rebel");
  if (markedSet.size === BINGO_SQUARES.length) types.add("blackout");
  return types;
};
const MASTER_INDICES = BINGO_SQUARES.map((_, index) => index);
const shuffled = (arr) => {
  const next = [...arr];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
};
// card 0 keeps the canonical layout so single-card play is unchanged; extra cards are shuffled deals
const dealCards = (count) => Array.from({ length: count }, (_, index) => {
  const order = index === 0 ? [...MASTER_INDICES] : shuffled(MASTER_INDICES);
  return { order, marked: new Set([order.indexOf(FREE_INDEX)]) };
});
const victoryTypesForCards = (cards) => {
  const types = new Set();
  cards.forEach((card) => victoryTypesFor(card.marked).forEach((type) => types.add(type)));
  return types;
};
const bestMarkedOf = (cards) => cards.reduce((best, card) => Math.max(best, card.marked.size), 0);
const sanitizeOrder = (value, fallbackIndex) => {
  const valid = Array.isArray(value) && value.length === 25 && new Set(value).size === 25
    && value.every((entry) => Number.isInteger(entry) && entry >= 0 && entry < 25);
  return valid ? [...value] : (fallbackIndex === 0 ? [...MASTER_INDICES] : shuffled(MASTER_INDICES));
};
const sanitizeCards = (value = {}) => {
  const count = Math.min(4, Math.max(1, Number.isInteger(value.card_count) ? value.card_count : 1));
  const orders = Array.isArray(value.card_orders) ? value.card_orders : [];
  const slots = Array.isArray(value.marked_slots) ? value.marked_slots : [];
  return Array.from({ length: count }, (_, index) => {
    const order = sanitizeOrder(orders[index], index);
    const freeSlot = order.indexOf(FREE_INDEX);
    const rawSlots = Array.isArray(slots[index]) ? slots[index] : (index === 0 && Array.isArray(value.marked) ? value.marked : []);
    const valid = rawSlots.filter((entry) => Number.isInteger(entry) && entry >= 0 && entry < 25);
    return { order, marked: new Set([freeSlot, ...valid]) };
  });
};
const sanitizeProgress = (value = {}) => ({
  score: Math.max(0, Math.min(START_SCORE, Number.isInteger(value.score) ? value.score : START_SCORE)),
  cards: sanitizeCards(value),
  victories: {
    corporate: Math.max(0, Number(value.victories?.corporate) || 0),
    rebel: Math.max(0, Number(value.victories?.rebel) || 0),
    blackout: Math.max(0, Number(value.victories?.blackout) || 0),
  },
});
const readLocalProgress = () => {
  try {
    return sanitizeProgress(JSON.parse(localStorage.getItem(LOCAL_PROGRESS_KEY) || "{}"));
  } catch {
    return sanitizeProgress();
  }
};
const VOICE_PACKS = {
  overseer: { label: "OVERSEER", detail: "STERILE CORPORATE", pitch: 0.72, rate: 0.82 },
  warden: { label: "WARDEN", detail: "AUTHORITARIAN", pitch: 0.35, rate: 0.72 },
  glitch: { label: "GLITCH", detail: "REBEL STATIC", pitch: 0.2, rate: 0.66 },
};
const BOARD_THEMES = {
  amber: { label: "WASTELAND AMBER" },
  crt: { label: "CRT GREEN" },
  corporate: { label: "CORPORATE WHITE" },
};
let bingoVoiceAudio;
let bingoChimeAudio;
const playChime = () => {
  bingoChimeAudio ||= new Audio("/assets/game-coin.wav");
  bingoChimeAudio.currentTime = 0;
  bingoChimeAudio.volume = 0.18;
  bingoChimeAudio.play().catch(() => {});
};
const speakSyntheticCall = (index, pack) => {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
  const [letter, number] = callFor(index).split("-");
  const profile = VOICE_PACKS[pack] || VOICE_PACKS.overseer;
  const utterance = new SpeechSynthesisUtterance(`Dolos Bingo. Caller transmission. ${letter} ${Number(number)}.`);
  const voices = window.speechSynthesis.getVoices();
  utterance.voice = voices.find((voice) => /en/i.test(voice.lang) && /male|daniel|david|alex|google uk english male/i.test(voice.name))
    || voices.find((voice) => /en/i.test(voice.lang))
    || null;
  utterance.pitch = profile.pitch;
  utterance.rate = profile.rate;
  utterance.volume = 0.92;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(utterance);
};
const playVoiceClip = (clip, pack, enabled, onEnded, siren = true) => {
  if (!enabled) return;
  if (siren) playChime();
  bingoVoiceAudio ||= new Audio();
  bingoVoiceAudio.pause();
  bingoVoiceAudio.src = `/assets/bingo-voice/${pack}/${clip}.mp3`;
  bingoVoiceAudio.volume = 0.92;
  const fallback = () => {
    if (clip.startsWith("call-")) speakSyntheticCall(Number(clip.slice(5)), pack);
    else onEnded?.();
  };
  bingoVoiceAudio.onended = () => onEnded?.();
  bingoVoiceAudio.onerror = fallback;
  bingoVoiceAudio.play().catch(fallback);
};
const speakCall = (index, enabled, pack, onEnded) => {
  if (!enabled || index === null) return;
  playVoiceClip(`call-${String(index).padStart(2, "0")}`, pack, true, () => {
    onEnded?.();
  });
};
const stopVoice = () => {
  if (bingoVoiceAudio) bingoVoiceAudio.pause();
  if (bingoChimeAudio) bingoChimeAudio.pause();
  if (typeof window !== "undefined" && "speechSynthesis" in window) window.speechSynthesis.cancel();
};
let bingoTickCtx;
const playTick = () => {
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return;
    bingoTickCtx ||= new Ctor();
    const osc = bingoTickCtx.createOscillator();
    const gain = bingoTickCtx.createGain();
    osc.type = "square";
    osc.frequency.value = 940;
    gain.gain.setValueAtTime(0.05, bingoTickCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0008, bingoTickCtx.currentTime + 0.08);
    osc.connect(gain).connect(bingoTickCtx.destination);
    osc.start();
    osc.stop(bingoTickCtx.currentTime + 0.09);
  } catch { /* audio unavailable */ }
};
const fireVictoryConfetti = (blackoutWin) => {
  const colors = ["#ffd23d", "#58ff9b", "#ff4a00", "#f3dfb5"];
  confetti({ particleCount: blackoutWin ? 260 : 150, spread: blackoutWin ? 120 : 78, origin: { y: 0.55 }, colors, scalar: 1.05 });
  if (blackoutWin) {
    window.setTimeout(() => confetti({ particleCount: 180, spread: 100, origin: { y: 0.35 }, colors }), 380);
  }
};

export function DolosComplianceBingo() {
  const [initialProgress] = useState(readLocalProgress);
  const [account, setAccount] = useState(() => getCachedUser());
  const [hydrated, setHydrated] = useState(false);
  const [saveState, setSaveState] = useState("LOCAL VAULT — SIGN IN TO SAVE");
  const [cards, setCards] = useState(initialProgress.cards);
  const [cardCount, setCardCount] = useState(initialProgress.cards.length);
  const [daubedThisCall, setDaubedThisCall] = useState(() => new Set());
  const [claimedWins, setClaimedWins] = useState(() => victoryTypesForCards(initialProgress.cards));
  const [victories, setVictories] = useState(initialProgress.victories);
  const [skipped, setSkipped] = useState(() => new Set());
  const [callHistory, setCallHistory] = useState([]);
  const [score, setScore] = useState(initialProgress.score);
  const [active, setActive] = useState(false);
  const [currentCall, setCurrentCall] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(ROUND_SECONDS);
  const [message, setMessage] = useState("");
  const [pulseMaster, setPulseMaster] = useState(null);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("dolopals-bingo-voice") !== "off");
  const [voicePack, setVoicePack] = useState(() => localStorage.getItem("dolopals-bingo-voice-pack") || "overseer");
  const [boardTheme, setBoardTheme] = useState(() => localStorage.getItem("dolopals-bingo-theme") || "amber");
  const [championAlerts, setChampionAlerts] = useState([]);
  const raceStartedAt = useRef(null);
  const [raceRefreshKey, setRaceRefreshKey] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [freezeLeft, setFreezeLeft] = useState(0);
  const [pingMaster, setPingMaster] = useState(null);
  const [powerUps, setPowerUps] = useState({ freeze: 1, ping: 1, mark: 1 });
  const [flashNonce, setFlashNonce] = useState(0);
  const [daubGear, setDaubGear] = useState(readDaubGear);

  const completedByCard = cards.map((card) => completedLinesFor(card.marked));
  const allCompleted = completedByCard.flat();
  const blackout = cards.some((card) => card.marked.size === BINGO_SQUARES.length);
  const rebelWin = allCompleted.some(isDiagonal);
  const corporateWin = allCompleted.some(isRow);
  const bestMarked = bestMarkedOf(cards);
  const victoryLabel = blackout ? "TOTAL APOCALYPSE" : rebelWin ? "REBEL VICTORY" : corporateWin ? "CORPORATE VICTORY" : null;
  const victoryDetail = blackout
    ? "Entire card blacked out before battery failure."
    : rebelWin
      ? "Underground path complete — the system was bypassed."
      : corporateWin
        ? "DOLOS path complete — efficiency protocol obeyed."
        : "";
  const oneAwayByCard = victoryLabel
    ? cards.map(() => new Set())
    : cards.map((card) => {
      const close = LINES.filter((line) => line.filter((slot) => card.marked.has(slot)).length === 4);
      return new Set(close.map((line) => line.find((slot) => !card.marked.has(slot))));
    });
  const oneAwayCard = oneAwayByCard.findIndex((targets) => targets.size > 0);
  const status = score <= 0
    ? "SYSTEM EJECTION — COMPLIANCE PTS DEPLETED"
    : blackout
      ? "TOTAL APOCALYPSE — ENTIRE CARD BLACKED OUT BEFORE BATTERY FAILURE"
      : rebelWin
        ? "REBEL VICTORY — UNDERGROUND PATH COMPLETE"
        : corporateWin
          ? "CORPORATE VICTORY — THE DOLOS PATH IS COMPLETE"
          : allCompleted.length
            ? `SYSTEM LINE COMPLETE — ${allCompleted.length} GRID ROUTE${allCompleted.length > 1 ? "S" : ""} VERIFIED`
            : message || (currentCall !== null
              ? `AWAITING MATCH — DAUB ${callFor(currentCall)} ON YOUR CARD${cards.length > 1 ? "S" : ""} BEFORE TIMER ZERO`
              : `CALLER STANDBY — ${bestMarked}/25 SECTORS MARKED`);

  useEffect(() => {
    const onExp = (event) => setDaubGear(readDaubGear(event.detail));
    window.addEventListener("dolo-expeditions", onExp);
    return () => window.removeEventListener("dolo-expeditions", onExp);
  }, []);

  useEffect(() => {
    let alive = true;
    const applyProgress = (raw) => {
      const progress = sanitizeProgress(raw);
      setCards(progress.cards);
      setCardCount(progress.cards.length);
      setClaimedWins(victoryTypesForCards(progress.cards));
      setVictories(progress.victories);
      setScore(progress.score);
      setSkipped(new Set());
      setCallHistory([]);
      setDaubedThisCall(new Set());
      setActive(false);
      setCurrentCall(null);
      setSecondsLeft(ROUND_SECONDS);
    };
    const loadForUser = async (user) => {
      if (!user) {
        setChampionAlerts([]);
        setSaveState("LOCAL VAULT — SIGN IN TO SAVE");
        return;
      }
      try {
        const { data } = await bingoApi.get("/dolopals/bingo");
        if (!alive) return;
        if (data.updated_at) {
          applyProgress(data);
          setSaveState(`ACCOUNT SYNCED — ${user.tag || user.name}`);
        } else {
          setSaveState(`ACCOUNT READY — ${user.tag || user.name}`);
        }
        const { data: alerts } = await bingoApi.get("/dolopals/bingo/notifications");
        if (alive && alerts.length) {
          setChampionAlerts(alerts);
          playChime();
        }
      } catch {
        if (alive) setSaveState("LOCAL VAULT — ACCOUNT SYNC RETRY PENDING");
      }
    };

    fetchMe()
      .then((user) => {
        if (!alive) return null;
        setAccount(user);
        return loadForUser(user);
      })
      .finally(() => {
        if (alive) setHydrated(true);
      });

    const onAuth = (event) => {
      const user = event.detail?.user || null;
      setAccount(user);
      loadForUser(user);
    };
    window.addEventListener("dolo-auth", onAuth);
    return () => {
      alive = false;
      window.removeEventListener("dolo-auth", onAuth);
    };
  }, []);

  useEffect(() => {
    if (!hydrated) return undefined;
    const payload = {
      score,
      marked: [...cards[0].marked].sort((a, b) => a - b),
      victories,
      card_count: cards.length,
      card_orders: cards.map((card) => card.order),
      marked_slots: cards.map((card) => [...card.marked].sort((a, b) => a - b)),
    };
    localStorage.setItem(LOCAL_PROGRESS_KEY, JSON.stringify(payload));
    if (!account) {
      setSaveState("LOCAL VAULT — SIGN IN TO SAVE");
      return undefined;
    }
    setSaveState("SYNCING ACCOUNT…");
    const timer = window.setTimeout(async () => {
      try {
        await bingoApi.put("/dolopals/bingo", payload);
        setSaveState(`ACCOUNT SYNCED — ${account.tag || account.name}`);
      } catch {
        setSaveState("LOCAL VAULT — ACCOUNT SYNC RETRY PENDING");
      }
    }, 650);
    return () => window.clearTimeout(timer);
  }, [account, hydrated, cards, score, victories]);

  useEffect(() => {
    const pulse = () => {
      const pool = MASTER_INDICES.filter((index) => index !== FREE_INDEX && cards.some((card) => !card.marked.has(card.order.indexOf(index))));
      setPulseMaster(pool.length ? randomFrom(pool) : null);
    };
    pulse();
    const interval = window.setInterval(pulse, 900);
    return () => window.clearInterval(interval);
  }, [cards]);

  useEffect(() => {
    if (!active || currentCall === null || score <= 0 || blackout) return undefined;
    if (freezeLeft > 0) {
      const freezeTimer = window.setTimeout(() => setFreezeLeft((value) => Math.max(0, value - 1)), 1000);
      return () => window.clearTimeout(freezeTimer);
    }
    if (secondsLeft === 0) {
      if (daubedThisCall.size > 0) {
        // at least one card daubed — the caller simply moves on, no penalty
        setMessage(`CALLER MOVES ON — ${callFor(currentCall)} DAUBED ON ${daubedThisCall.size}/${cards.length} CARD${cards.length > 1 ? "S" : ""}.`);
        const pool = availableCalls(cards, skipped);
        const nextCall = pool.length ? randomFrom(pool) : null;
        setCurrentCall(nextCall);
        setSecondsLeft(ROUND_SECONDS);
        setDaubedThisCall(new Set());
        if (nextCall !== null) setCallHistory((history) => [nextCall, ...history].slice(0, 24));
        speakCall(nextCall, voiceOn, voicePack);
        return undefined;
      }
      setStreak(0);
      const nextSkipped = new Set(skipped);
      nextSkipped.add(currentCall);
      let pool = availableCalls(cards, nextSkipped);
      if (!pool.length) {
        nextSkipped.clear();
        pool = availableCalls(cards, nextSkipped);
      }
      setSkipped(nextSkipped);
      setMessage(`TIME EXPIRED — ${callFor(currentCall)} RETURNED TO THE CALL QUEUE. STREAK LOST.`);
      const nextCall = pool.length ? randomFrom(pool) : null;
      setCurrentCall(nextCall);
      setSecondsLeft(ROUND_SECONDS);
      setDaubedThisCall(new Set());
      if (nextCall !== null) setCallHistory((history) => [nextCall, ...history].slice(0, 24));
      playVoiceClip("event-timeout", voicePack, voiceOn, () => speakCall(nextCall, voiceOn, voicePack));
      return undefined;
    }
    const timer = window.setTimeout(() => setSecondsLeft((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [active, blackout, cards, currentCall, daubedThisCall, freezeLeft, score, secondsLeft, skipped, voiceOn, voicePack]);

  useEffect(() => {
    if (active && voiceOn && freezeLeft === 0 && secondsLeft > 0 && secondsLeft <= 5) playTick();
  }, [active, voiceOn, freezeLeft, secondsLeft]);

  const startCalls = () => {
    if (blackout || score <= 0) {
      resetGrid();
      return;
    }
    const pool = availableCalls(cards, skipped);
    if (!pool.length) return;
    const nextCall = randomFrom(pool);
    setActive(true);
    setCurrentCall(nextCall);
    setSecondsLeft(ROUND_SECONDS);
    setDaubedThisCall(new Set());
    setCallHistory((history) => [nextCall, ...history].slice(0, 24));
    setMessage("");
    raceStartedAt.current = Date.now();
    playVoiceClip("event-start", voicePack, voiceOn, () => speakCall(nextCall, voiceOn, voicePack));
  };

  const resetGrid = (count = cardCount) => {
    stopVoice();
    raceStartedAt.current = null;
    setCards(dealCards(count));
    setCardCount(count);
    setClaimedWins(new Set());
    setSkipped(new Set());
    setCallHistory([]);
    setDaubedThisCall(new Set());
    setScore(START_SCORE);
    setActive(false);
    setCurrentCall(null);
    setSecondsLeft(ROUND_SECONDS);
    setMessage("");
    setStreak(0);
    setBestStreak(0);
    setFreezeLeft(0);
    setPingMaster(null);
    setPowerUps({ freeze: 1, ping: 1, mark: 1 });
  };

  const changeCardCount = (count) => {
    if (count === cardCount || active) return;
    resetGrid(count);
    setMessage(count > 1 ? `${count} CARDS DEALT — DAUB EACH CALL ON EVERY CARD YOU CAN FIND IT ON.` : "SINGLE CARD DEALT — CLASSIC PLAY.");
  };

  const registerWins = (nextCards) => {
    const types = victoryTypesForCards(nextCards);
    const fresh = [...types].filter((type) => !claimedWins.has(type));
    if (!fresh.length) return [];
    setVictories((current) => {
      const next = { ...current };
      fresh.forEach((type) => { next[type] += 1; });
      return next;
    });
    setClaimedWins((current) => new Set([...current, ...types]));
    return fresh;
  };

  const submitRaceWin = (type, markedCount) => {
    if (!account) return;
    const elapsed = raceStartedAt.current ? Math.max(1, Math.round((Date.now() - raceStartedAt.current) / 1000)) : 1;
    bingoApi.post("/dolopals/bingo/race/claim", {
      victory_type: type,
      elapsed_seconds: elapsed,
      score,
      blackout_progress: markedCount,
    }).catch(() => {}).finally(() => setRaceRefreshKey((value) => value + 1));
  };

  const markCurrentOn = (cardIndices) => {
    if (currentCall === null) return;
    const call = currentCall;
    const targets = cardIndices.filter((ci) => {
      const card = cards[ci];
      return card && !card.marked.has(card.order.indexOf(call));
    });
    if (!targets.length) return;
    const nextCards = cards.map((card, ci) => {
      if (!targets.includes(ci)) return card;
      const next = new Set(card.marked);
      next.add(card.order.indexOf(call));
      return { ...card, marked: next };
    });
    setCards(nextCards);
    const nextSkipped = new Set(skipped);
    nextSkipped.delete(call);
    setSkipped(nextSkipped);
    let running = streak;
    let reward = 0;
    targets.forEach(() => {
      running += 1;
      reward += Math.min(running * 2, 10);
    });
    setStreak(running);
    setBestStreak((best) => Math.max(best, running));
    setScore((value) => Math.min(START_SCORE, value + reward));
    const daubed = new Set(daubedThisCall);
    targets.forEach((ci) => daubed.add(ci));
    setDaubedThisCall(daubed);
    setMessage(targets.length > 1
      ? `MULTI-DAUB — ${callFor(call)} LOGGED ON ${targets.length} CARDS. +${reward} PTS · STREAK ×${running}.`
      : `CORRECT MATCH — ${callFor(call)} LOGGED ON CARD ${targets[0] + 1}. +${reward} PTS · STREAK ×${running}.`);
    const freshWins = registerWins(nextCards);
    if (freshWins.length) {
      celebrate(freshWins.includes("blackout"));
      freshWins.forEach((type) => submitRaceWin(type, bestMarkedOf(nextCards)));
    }
    const eventClip = freshWins.includes("blackout")
      ? "event-blackout"
      : freshWins.includes("rebel")
        ? "event-rebel"
        : freshWins.includes("corporate")
          ? "event-corporate"
          : "event-correct";

    if (nextCards.some((card) => card.marked.size === BINGO_SQUARES.length)) {
      setActive(false);
      setCurrentCall(null);
      setDaubedThisCall(new Set());
      playVoiceClip(eventClip, voicePack, voiceOn);
      return;
    }
    const allDaubed = nextCards.every((card) => card.marked.has(card.order.indexOf(call)));
    if (allDaubed) {
      const pool = availableCalls(nextCards, nextSkipped);
      const nextCall = pool.length ? randomFrom(pool) : null;
      setCurrentCall(nextCall);
      setSecondsLeft(ROUND_SECONDS);
      setDaubedThisCall(new Set());
      if (nextCall !== null) setCallHistory((history) => [nextCall, ...history].slice(0, 24));
      playVoiceClip(eventClip, voicePack, voiceOn, () => speakCall(nextCall, voiceOn, voicePack));
      return;
    }
    // call stays live — the caller holds while the player hunts the other cards
    playVoiceClip(eventClip, voicePack, voiceOn);
  };

  const chooseSquare = (cardIdx, slot) => {
    setPulseMaster(null);
    const card = cards[cardIdx];
    if (!card) return;
    const master = card.order[slot];
    if (master === FREE_INDEX || card.marked.has(slot)) return;
    if (!active || currentCall === null) {
      setMessage("CALLER STANDBY — START THE NEXT DOLOS CALL BEFORE MARKING A SECTOR.");
      return;
    }
    if (master !== currentCall) {
      const nextScore = Math.max(0, score - 5);
      setScore(nextScore);
      setStreak(0);
      setMessage(`WRONG SECTOR — ${callFor(master)} SELECTED ON CARD ${cardIdx + 1}. -5 COMPLIANCE PTS. STREAK RESET.`);
      playVoiceClip("event-wrong", voicePack, voiceOn);
      if (nextScore === 0) {
        setActive(false);
        setCurrentCall(null);
        setDaubedThisCall(new Set());
      }
      return;
    }
    markCurrentOn([cardIdx]);
  };

  const nextCallEarly = () => {
    if (!active || currentCall === null || daubedThisCall.size === 0) return;
    setMessage(`CALLER MOVES ON — ${callFor(currentCall)} DAUBED ON ${daubedThisCall.size}/${cards.length} CARD${cards.length > 1 ? "S" : ""}.`);
    const pool = availableCalls(cards, skipped);
    const nextCall = pool.length ? randomFrom(pool) : null;
    setCurrentCall(nextCall);
    setSecondsLeft(ROUND_SECONDS);
    setDaubedThisCall(new Set());
    if (nextCall !== null) setCallHistory((history) => [nextCall, ...history].slice(0, 24));
    speakCall(nextCall, voiceOn, voicePack);
  };

  const shareVictory = () => {
    if (!victoryLabel) return;
    downloadBingoVictoryCard({
      player: account?.name || "WASTELAND RUNNER",
      tag: account?.tag || "GUEST TAG",
      victory: victoryLabel,
      score,
      marked: bestMarked,
      calls: Math.max(0, bestMarked - 1),
    });
  };

  const celebrate = (blackoutWin) => {
    fireVictoryConfetti(blackoutWin);
    setFlashNonce(Date.now());
  };

  const useFreeze = () => {
    if (!active || currentCall === null || powerUps.freeze < 1) return;
    setPowerUps((current) => ({ ...current, freeze: 0 }));
    setFreezeLeft(5);
    setMessage("CRYO FREEZE ENGAGED — CALLER TIMER SUSPENDED FOR 5 SECONDS.");
  };

  const usePing = () => {
    if (!active || currentCall === null || powerUps.ping < 1) return;
    setPowerUps((current) => ({ ...current, ping: 0 }));
    setPingMaster(currentCall);
    setMessage(`SIGNAL PING — ${callFor(currentCall)} SECTOR${cards.length > 1 ? "S" : ""} ILLUMINATED ON EVERY CARD.`);
    window.setTimeout(() => setPingMaster(null), 2800);
  };

  const useAutoMark = () => {
    if (!active || currentCall === null || powerUps.mark < 1) return;
    setPowerUps((current) => ({ ...current, mark: 0 }));
    markCurrentOn(cards.map((_, index) => index));
  };

  const toggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    localStorage.setItem("dolopals-bingo-voice", next ? "on" : "off");
    if (!next) stopVoice();
  };

  const changeVoicePack = (pack) => {
    setVoicePack(pack);
    localStorage.setItem("dolopals-bingo-voice-pack", pack);
    stopVoice();
  };

  const changeBoardTheme = (theme) => {
    setBoardTheme(theme);
    localStorage.setItem("dolopals-bingo-theme", theme);
  };

  const previewVoicePack = () => {
    stopVoice();
    playVoiceClip("event-sample", voicePack, true, undefined, false);
  };

  return (
    <section id="dolo-bingo" className="dolos-bingo" data-theme={boardTheme} data-daub={daubGear.daub || undefined} data-stamp={daubGear.stamp || undefined} data-testid="dolos-bingo-section">
      <GameFullscreenButton testid="dolo-bingo-fullscreen" />
      {flashNonce ? <div key={flashNonce} className="bingo-win-flash" aria-hidden="true" data-testid="bingo-win-flash" /> : null}
      <div className="bingo-terminal">
        <SectionHeading
          eyebrow="DOLOS // WASTELAND BINGO"
          title="Dolos Bingo."
          text="Welcome to the DOLOS System Compliance & Survival Grid. Channeling a sleek, dystopian, corporate-authoritarian interface where AI-driven social regulation, data optimization, and harsh post-crisis realities collide, this bingo game tests your ability to survive the algorithm, manage your metrics, and dodge total system termination."
        />
        <div className="bingo-objective" data-testid="bingo-objective">
          <span>OBJECTIVE</span>
          <strong>Complete a row, column, or diagonal before your EXP hits zero or the wasteland claims you.</strong>
        </div>
        <div className="bingo-caller" data-testid="bingo-caller-panel">
          <div className="bingo-call-box bingo-call-box--ball">
            <span>BALL DRAWN</span>
            {currentCall !== null ? (
              <b className="bingo-ball" data-col={currentCall % 5} aria-label={callFor(currentCall)} data-testid="bingo-current-call">
                <i aria-hidden="true">{CALL_LETTERS[currentCall % 5]}</i>
                {numberFor(currentCall)}
              </b>
            ) : (
              <strong data-testid="bingo-current-call">STANDBY</strong>
            )}
            {currentCall !== null ? <em className="bingo-call-event" data-testid="bingo-call-event">{BINGO_SQUARES[currentCall].title}</em> : null}
          </div>
          <div className={`bingo-call-box ${freezeLeft > 0 ? "is-frozen" : ""}`}>
            <span>TIME WINDOW</span>
            <strong data-testid="bingo-timer">{secondsLeft}S</strong>
            <div className="bingo-timer-bar" aria-hidden="true"><i style={{ width: `${(secondsLeft / ROUND_SECONDS) * 100}%` }} /></div>
            {freezeLeft > 0 ? <small className="bingo-cryo" data-testid="bingo-cryo-status">CRYO ACTIVE — {freezeLeft}S</small> : null}
          </div>
          <div className="bingo-call-box">
            <span>SYSTEM PTS</span>
            <strong data-testid="bingo-score">{score}</strong>
            <small data-testid="bingo-blackout-progress">BLACKOUT {bestMarked}/25</small>
            <small className={`bingo-streak ${streak >= 3 ? "is-hot" : ""}`} data-testid="bingo-streak-meter">
              <Flame size={11} aria-hidden="true" /> STREAK ×{streak}{streak >= 2 ? ` · +${Math.min(streak * 2, 10)} PTS/MATCH` : ""}{bestStreak >= 3 ? ` · BEST ×${bestStreak}` : ""}
            </small>
          </div>
          <div className="bingo-start-stack">
            <button type="button" className="bingo-start" onClick={startCalls} disabled={active} data-testid="bingo-start-button">
              {active ? "CALL ACTIVE" : blackout || score <= 0 ? "REBOOT GRID" : "START CALLS"}
            </button>
            {active && daubedThisCall.size > 0 ? (
              <button type="button" className="bingo-next" onClick={nextCallEarly} data-testid="bingo-next-call-button">
                NEXT CALL ▸ {daubedThisCall.size}/{cards.length} DAUBED
              </button>
            ) : null}
          </div>
        </div>
        <div className="bingo-tray" data-testid="bingo-call-tray">
          <span className="bingo-tray-label">CALLED BALLS</span>
          <div className="bingo-tray-balls">
            {callHistory.length
              ? callHistory.slice(0, 10).map((index, order) => (
                <span
                  key={`${index}-${order}`}
                  className={`bingo-mini-ball ${order === 0 ? "is-latest" : ""} ${cards.every((card) => card.marked.has(card.order.indexOf(index))) ? "is-daubed" : ""}`}
                  data-col={index % 5}
                  data-testid={`bingo-tray-ball-${order}`}
                >
                  {callFor(index)}
                </span>
              ))
              : <em data-testid="bingo-tray-empty">NO BALLS DRAWN YET</em>}
          </div>
          <div className="bingo-cardcount" data-testid="bingo-card-count">
            <span>CARDS</span>
            {[1, 2, 3, 4].map((count) => (
              <button
                key={count}
                type="button"
                className={cardCount === count ? "is-active" : ""}
                onClick={() => changeCardCount(count)}
                disabled={active}
                aria-pressed={cardCount === count}
                data-testid={`bingo-card-count-${count}`}
              >
                {count}
              </button>
            ))}
          </div>
        </div>
        <div className="bingo-powerups" data-testid="bingo-powerups">
          <span className="bingo-powerups-label">TACTICAL SURGE — ONE USE EACH</span>
          <button type="button" onClick={useFreeze} disabled={!active || currentCall === null || powerUps.freeze < 1} data-testid="bingo-powerup-freeze">
            <Snowflake size={13} aria-hidden="true" /> CRYO FREEZE {powerUps.freeze ? "· 5S HOLD" : "· SPENT"}
          </button>
          <button type="button" onClick={usePing} disabled={!active || currentCall === null || powerUps.ping < 1} data-testid="bingo-powerup-ping">
            <Radar size={13} aria-hidden="true" /> SIGNAL PING {powerUps.ping ? "· REVEAL SECTOR" : "· SPENT"}
          </button>
          <button type="button" onClick={useAutoMark} disabled={!active || currentCall === null || powerUps.mark < 1} data-testid="bingo-powerup-mark">
            <Zap size={13} aria-hidden="true" /> AUTO MARK {powerUps.mark ? "· FREE DAUB" : "· SPENT"}
          </button>
        </div>
        <div className="bingo-save-strip" data-testid="bingo-save-strip">
          <span data-testid="bingo-save-state">{saveState}</span>
          <span data-testid="bingo-victory-counts">CORPORATE ×{victories.corporate} · REBEL ×{victories.rebel} · BLACKOUT ×{victories.blackout}</span>
          <button type="button" className="bingo-voice-toggle" onClick={toggleVoice} data-testid="bingo-voice-toggle" aria-pressed={voiceOn}>
            {voiceOn ? <Volume2 size={13} /> : <VolumeX size={13} />} VOICE {voiceOn ? "ON" : "OFF"}
          </button>
          <label className="bingo-voice-pack">
            VOICE PACK
            <select value={voicePack} onChange={(event) => changeVoicePack(event.target.value)} data-testid="bingo-voice-pack-select">
              {Object.entries(VOICE_PACKS).map(([key, pack]) => {
                const label = `${pack.label} — ${pack.detail}`;
                return <option key={key} value={key}>{label}</option>;
              })}
            </select>
          </label>
          <button type="button" className="bingo-voice-toggle bingo-voice-preview" onClick={previewVoicePack} data-testid="bingo-voice-preview-button">
            <Volume2 size={13} /> PLAY SAMPLE
          </button>
          <label className="bingo-voice-pack bingo-theme-picker">
            THEME
            <select value={boardTheme} onChange={(event) => changeBoardTheme(event.target.value)} data-testid="bingo-theme-select">
              {Object.entries(BOARD_THEMES).map(([key, theme]) => {
                const label = theme.label;
                return <option key={key} value={key}>{label}</option>;
              })}
            </select>
          </label>
          {!account ? <Link to="/account" data-testid="bingo-signin-link">SIGN IN TO SAVE</Link> : null}
        </div>
        {championAlerts.length ? (
          <div className="bingo-champion-alerts" role="status" data-testid="bingo-champion-alerts">
            {championAlerts.map((alert, index) => (
              <p key={`${alert.badge}-${alert.created_at}`} data-testid={`bingo-champion-alert-${index}`}>
                <Crown size={15} /> {alert.message}
              </p>
            ))}
          </div>
        ) : null}
        <div className="bingo-layout">
          <div className="bingo-board" data-testid="bingo-board">
            {oneAwayCard >= 0 ? (
              <div className="bingo-oneaway" role="status" data-testid="bingo-oneaway-banner">
                <Flame size={14} aria-hidden="true" />
                {cards.length > 1 ? `CARD ${oneAwayCard + 1} IS ONE AWAY` : "ONE AWAY"} — MARK {[...oneAwayByCard[oneAwayCard]].map((slot) => callFor(cards[oneAwayCard].order[slot])).join(" / ")} TO COMPLETE A GRID LINE
              </div>
            ) : null}
            <div className={`bingo-cards ${cards.length > 1 ? "is-multi" : ""}`} data-testid="bingo-cards">
              {cards.map((card, cardIdx) => {
                const winningSlots = new Set(completedByCard[cardIdx].flat());
                const cardOneAway = oneAwayByCard[cardIdx];
                return (
                  <div className="bingo-card" key={cardIdx} data-testid={`bingo-card-${cardIdx}`}>
                    {cards.length > 1 ? (
                      <span className={`bingo-card-tag ${completedByCard[cardIdx].length ? "is-winning" : ""}`} data-testid={`bingo-card-tag-${cardIdx}`}>
                        CARD {cardIdx + 1}{completedByCard[cardIdx].length ? " — LINE COMPLETE" : ""}
                      </span>
                    ) : null}
                    <div className="bingo-columns" aria-hidden="true">
                      {BINGO_COLUMNS.map((column, index) => (
                        <span key={column} data-col={index}><b>{CALL_LETTERS[index]}</b><small>{column}</small></span>
                      ))}
                    </div>
                    <div className="bingo-grid" role="grid" aria-label={`Dolos Bingo card ${cardIdx + 1}`}>
                      {card.order.map((master, slot) => {
                        const square = BINGO_SQUARES[master];
                        const isMarked = card.marked.has(slot);
                        return (
                          <button
                            type="button"
                            key={`${cardIdx}-${master}`}
                            className={`bingo-square ${isMarked ? "is-marked" : ""} ${square.free ? "is-free" : ""} ${winningSlots.has(slot) ? "is-winning" : ""} ${!isMarked && pulseMaster === master ? "is-pulsing" : ""} ${!isMarked && pingMaster === master ? "is-pinged" : ""} ${!isMarked && cardOneAway.has(slot) ? "is-oneaway" : ""}`}
                            onClick={() => chooseSquare(cardIdx, slot)}
                            disabled={square.free}
                            aria-pressed={isMarked}
                            data-testid={cardIdx === 0 ? `bingo-square-${slot}` : `bingo-square-${cardIdx}-${slot}`}
                          >
                            {square.free ? null : <b className="bingo-square-code">{numberFor(master)}</b>}
                            {square.free ? <Crown className="bingo-crown" size={16} aria-hidden="true" /> : null}
                            <span className="bingo-square-title">{square.title}</span>
                            <small>{square.detail}</small>
                            {isMarked && !square.free ? <span className="bingo-daub" aria-hidden="true" /> : null}
                            {isMarked ? <span className="bingo-check" aria-hidden="true"><Check size={14} /></span> : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            {victoryLabel ? (
              <div className="bingo-winner-banner" role="status" data-testid="bingo-winner-banner">
                <Crown size={28} aria-hidden="true" />
                <div>
                  <span>VICTORY CONFIRMED</span>
                  <strong>YOU HAVE COMPLETED THE TASK</strong>
                  <small>Proceeds to credit payout. {victoryLabel} · {bestMarked}/25 sectors · {score} system PTS.</small>
                </div>
              </div>
            ) : null}
            <div className="bingo-console">
              <strong data-testid="bingo-status">{status}</strong>
              <div className="bingo-console-actions">
                {victoryLabel ? (
                  <button type="button" className="bingo-share" onClick={shareVictory} data-testid="bingo-share-button">
                    <Download size={13} /> SHARE VICTORY CARD
                  </button>
                ) : null}
                <button type="button" onClick={() => resetGrid()} data-testid="bingo-reset-button">
                  <RotateCcw size={13} /> RESET GRID
                </button>
              </div>
            </div>
          </div>
          <aside className="bingo-rules" data-testid="bingo-rules">
            <h3>How to Play:</h3>
            <ol>
              <li><strong>The Digital Dystopia Rules:</strong> Print this or pull it up on a screen next to your terminal.</li>
              <li><strong>Answer the Call:</strong> When DOLOS calls a letter-number, daub the matching square on every card you can find it on before the 30-second window closes — each daub banks streak PTS. A wrong sector costs 5 compliance PTS; a full timeout sends the call to the back of the queue.</li>
              <li><strong>Multi-Card Hall Play:</strong> Deal up to 4 cards with the CARDS picker. Daub at least one card and the caller moves on clean — or hit NEXT CALL to keep the hall pace. The caller holds while you hunt the rest.</li>
              <li><strong>Mark Your Squares:</strong> Cross off sectors as matching events occur during narrative management games, survival sims, or your day-to-day corporate grind.</li>
            </ol>
            <h4>Winning Conditions:</h4>
            <ul>
              <li><strong>Corporate Victory (The DOLOS Path):</strong> Get a horizontal line by strictly obeying efficiency and system rules.</li>
              <li><strong>Rebel Victory (The Underground Path):</strong> Get a diagonal line by uncovering secrets, bypassing filters, and helping people slip through the cracks.</li>
              <li><strong>Total Apocalypse:</strong> Blackout the entire card before your battery dies.</li>
            </ul>
          </aside>
        </div>
        <BingoRaceBoard refreshKey={raceRefreshKey} signedIn={!!account} />
        <BingoChampionHistory refreshKey={raceRefreshKey} />
        <BingoLeaderboard refreshKey={victories.corporate + victories.rebel + victories.blackout} />
      </div>
    </section>
  );
}
